<template>
  <div class="generated-image-block">
    <div class="block-container">
      <BaseImage
        alt="image"
        class="generated-image"
        v-for="image in data.images"
        :key="image.id"
        :src="image.url"
        @click="openViewerImage(image)"
      />
    </div>
    <div class="actions-section" v-if="!isUserMessage">
      <BbBtn
        type="primary"
        class="change-aspect-style-button"
        color="#6222e3"
        :is-disabled="disabledChangeAspectOrRatioButton"
        @click="handleInjectClick"
      >
        {{ t('conversation.imageParams.changeAspectOrStyle') }}
      </BbBtn>
    </div>
    <BbPopup v-model="showPreviewModal" width="800px">
      <UserImagePreview
        :image-id="selectedImage.id"
        :in-conversation-context="true"
        :prompt="isUserMessage ? currentMessage.message : selectedImage.userQuery"
        @close="showPreviewModal = false"
        @download="onDownload(selectedImage)"
        @remix="(image) => onRemix(image)"
      />
    </BbPopup>
  </div>
</template>
<script>
import { computed, onMounted, ref } from 'vue';
import throttle from 'lodash/throttle';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { SetQueryValue } from '~/constants/mutations';
import { Role } from '~/constants/conversationModels';
import { Routes } from '~/constants/routes';
import { GetUserData } from '~/constants/getters';

import useImageGeneration from '~/composables/useImageGeneration';
import useConversations from '~/composables/useConversations';
import BaseImage from '~/components/common/BaseImage.vue';
import UserImagePreview from '~/components/userImages/UserImagePreview.vue';

export default {
  components: { UserImagePreview, BaseImage },
  props: {
    messageId: {
      type: String,
    },
    data: {
      type: Object,
      default: () => {},
    },
    type: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    images: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const { t } = useI18n();
    const store = useStore();
    const {
      updateGeneratedImagesData,
      setUserImageForRemix,
      downloadUserImage,
      injectImageParamsRequestMessageIntoConversation,
    } = useImageGeneration();
    const { currentConversation } = useConversations();
    const route = useRoute();

    const currentRouteName = route.name;
    const showPreviewModal = ref(false);
    const selectedImage = ref(null);
    const processing = ref(false);
    const isAssistConversation = currentRouteName === Routes.Conversation.name;

    const currentMessage = computed(() =>
      currentConversation.value.messages?.find((msg) => msg.messageId === props.messageId),
    );
    const isUserMessage = computed(() => currentMessage.value.role === Role.user);
    const userData = computed(() => store.getters[`authStore/${GetUserData}`]);
    const inputsDisabled = computed(
      () =>
        isAssistConversation &&
        currentConversation.value?.userId &&
        currentConversation.value?.userId !== userData.value?.id,
    );
    const disabledChangeAspectOrRatioButton = computed(() => {
      const lastMessage = currentConversation.value.messages?.slice(-1)[0];

      return inputsDisabled.value || lastMessage.data?.[0]?.type === 'imageStyleChoices' || processing.value;
    });

    const doInject = async () => {
      if (processing.value) return;
      processing.value = true;
      try {
        const res = injectImageParamsRequestMessageIntoConversation(props.messageId);
        if (res && typeof res.then === 'function') {
          await res;
        }
      } catch (e) {
        console.log('error:', e);
      } finally {
        // clear processing when the injection finishes so we don't allow concurrent runs
        processing.value = false;
      }
    };

    const handleInjectClick = throttle(doInject, 300, { leading: true, trailing: false });

    const onRemix = async (image) => {
      const userRemixObject = {
        query: image.userQuery,
        remixImage: image.url,
        remixImageId: image.id,
      };
      setUserImageForRemix({ ...image, ...userRemixObject });
      store.dispatch(`conversationStore/${SetQueryValue}`, userRemixObject);
      showPreviewModal.value = false;
    };

    const onDownload = async (selectedImage) => {
      const title = selectedImage.name || 'Generetad_Image.png';
      const userConversationId = currentConversation?.value?.userId || '';
      await downloadUserImage(selectedImage.id, title, userConversationId);
    };

    const openViewerImage = (image) => {
      showPreviewModal.value = true;
      selectedImage.value = image;
    };

    onMounted(async () => {
      // get images from s3 bucket
      const userConversationId = currentConversation?.value?.userId || '';
      await updateGeneratedImagesData(props.messageId, props.data, userConversationId);
    });

    return {
      injectImageParamsRequestMessageIntoConversation,
      t,
      openViewerImage,
      selectedImage,
      showPreviewModal,
      onDownload,
      onRemix,
      isUserMessage,
      currentMessage,
      disabledChangeAspectOrRatioButton,
      handleInjectClick,
    };
  },
};
</script>

<style scoped lang="scss">
.block-container {
  display: flex;
  flex-direction: row;
  gap: $bb-spacing-2;

  .generated-image {
    width: 100px;
    height: 100px;

    :deep(img) {
      max-height: 200px !important;
    }
  }
}

.actions-section {
  margin-top: $bb-spacing-2;
  display: flex;
  justify-content: flex-end;

  .change-aspect-style-button {
    border-radius: 100px;
  }
}
</style>
